'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';

import {
  ImageSliderFullScreen,
  getImageUrl,
  getImageSources,
  getImagePictureSizes,
} from '@/app/components/hotel-detail/ImageSlider';
import { getImageSources as getGridImageSources } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import customCss from '@/app/components/hotel-detail/css/customCss';
import CloseIcon from '@/app/components/search/components/Modal/CloseIcon';
import { modalCloseButton } from '@/app/components/search/components/Modal/closeButtonStyles';
import { focusRing } from '@/app/components/search/styles/mixins';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { getPhotoAlt } from '@/app/components/hotel-detail/getPhotoAlt';
import { useIsMobile } from '@/app/components/search/utils/useSsrMedia';
import { Icon } from '@/app/common/icons/Icon';

// #region types & helpers

type View = 'grid' | 'photo';

type Props = {
  images: string[];
  // Hotel name, used in each photo's alt text.
  name?: string;
  isOpen: boolean | undefined;
  initialImageIndex?: number;
  onDismiss: () => void;
  // Re-open handler for browser forward-navigation. When provided, the modal
  // participates in browser history so the back button closes it first.
  onReopen?: () => void;
  // Unique modal id. Must differ per mounted instance (hero vs. hotel-details
  // gallery) — a shared id makes both instances' browser-history hooks match the
  // same ?modal= entry and open together (double panel + stacked backdrop).
  modalId?: string;
};

// #endregion

// #region styled components

const Header = styled.div<{ $photo?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  flex-shrink: 0;
  padding: 0 16px;
  position: relative;

  /* Desktop side-sheet header: bordered + elevated. The fullscreen photo
     lightbox has no header chrome (matches Figma), and mobile stays borderless. */
  ${(p) =>
    !p.$photo &&
    css`
      @media (min-width: 768px) {
        border-bottom: 1px solid ${p.theme.kallisto.color.border.static.borderNeutral};
        box-shadow: ${p.theme.kallisto.shadow.mediumDownShadow};
      }
    `}
`;

// Absolutely placed because this Header centres its title; the rest is the standard
// modal close button.
const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  ${modalCloseButton}
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const LeftArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-width: 0;
  outline: none;
`;

// Grid view

const PhotoGrid = styled.div`
  display: grid;
  gap: 8px;
  padding: 16px 16px 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media ${customCss.breakpoints.sm} {
    padding: 24px;
  }
`;

// Photo view

const PhotoViewWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
`;

const SliderWrap = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  transform: translateZ(0);
`;

const NavArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === 'left' ? 'left: 24px;' : 'right: 24px;')}
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  cursor: pointer;
  background: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};

  &:hover {
    background: ${(p) => p.theme.kallisto.color.background.static.backgroundNeutralSubtle};
  }

  & > img {
    width: 20px;
    height: 20px;
    ${(p) => p.$side === 'right' && 'transform: rotate(180deg);'}
  }

  &:focus-visible {
    ${focusRing}
  }
`;

const PhotoButton = styled.button`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: none;
  cursor: pointer;

  &:focus-visible {
    ${focusRing}
  }
`;

// Bottom sticky bar holding the centered counter (matches Figma StickySurface-DS, 64px)
const Counter = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.kallisto.mixins.font('Title5')};
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
  flex-shrink: 0;
`;

// #endregion

export default function EvoGalleryModal({
  images: rawImages,
  name,
  isOpen,
  initialImageIndex = 0,
  onDismiss,
  onReopen,
  modalId = 'evo-gallery-modal',
}: Props) {
  const images = useMemo(() => Array.from(new Set(rawImages)), [rawImages]);
  const [view, setView] = useState<View>('grid');
  const [currentIndex, setCurrentIndex] = useState(initialImageIndex);
  // The slide the photo view opened at. On mobile the slider is left UNCONTROLLED
  // (imageIndex stays fixed) so swiping is fluid and never fights a re-scroll;
  // currentIndex only follows for the counter. Desktop drives it via the arrows.
  const [photoStartIndex, setPhotoStartIndex] = useState(0);
  const leftAreaRef = useRef<HTMLDivElement>(null);
  const isInitializingRef = useRef(false);
  const targetIndexRef = useRef(0);

  const { localization, commonData } = usePageWrapperData();
  const { t } = useTranslation();
  const isMobile = useIsMobile(commonData.isMobileDevice) ?? false;
  const brand = localization.brand as Brand;

  // Reset to grid view each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setView('grid');
      setCurrentIndex(initialImageIndex ?? 0);
    }
  }, [isOpen, initialImageIndex]);

  // react-remove-scroll (Radix hotel-detail modal) registers a bubble-phase wheel
  // listener on document that cancels events outside its lock boundary. stopPropagation
  // here prevents it from ever seeing gallery wheel events.
  useEffect(() => {
    const el = leftAreaRef.current;
    if (!el) {
      return undefined;
    }
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: true });
    return () => el.removeEventListener('wheel', stop);
  }, [isOpen]);

  // Grid scrolling is native: LeftArea is the focusable scroll container (see its
  // tabIndex + the focus effect below), so Arrow/Space/PageUp-Down scroll it without
  // a global key handler — which previously hijacked browser shortcuts. This handler
  // only covers the two behaviours the browser can't give us: two-level Escape
  // (photo → grid → close) and left/right photo navigation in the slider.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        if (view === 'photo') {
          setView('grid');
        } else {
          onDismiss();
        }
        return;
      }

      // Desktop only: on mobile the slider runs in swipe (scroll) mode and handles
      // arrow keys itself (useKeyNavigation) — running this too would double-advance.
      if (
        !isMobile &&
        view === 'photo' &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
      ) {
        e.preventDefault();
        // This capture-phase handler is the single source of truth for arrow nav.
        // Stop the slider's always-on useKey (HeroGallerySliderOverlay) from also
        // firing on the same press, which would otherwise double-handle it.
        e.stopImmediatePropagation();
        const delta = e.key === 'ArrowLeft' ? -1 : 1;
        setCurrentIndex((idx) => {
          const next = (idx + delta + images.length) % images.length;
          isInitializingRef.current = true;
          targetIndexRef.current = next;
          return next;
        });
      }
    };

    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, [isOpen, view, onDismiss, images.length, isMobile]);

  // Focus the scroll container when the grid opens so keyboard scrolling targets it
  // natively (instead of the scroll-locked body, which is why scrolling did nothing).
  useEffect(() => {
    if (isOpen && view === 'grid') {
      const id = window.requestAnimationFrame(() => leftAreaRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isOpen, view]);

  const openPhoto = (index: number) => {
    isInitializingRef.current = true;
    targetIndexRef.current = index;
    setCurrentIndex(index);
    setPhotoStartIndex(index);
    setView('photo');
  };

  const onSlideChange = (index: number) => {
    if (isInitializingRef.current) {
      if (index === targetIndexRef.current) {
        isInitializingRef.current = false;
      } else {
        return; // stale callback from HeroGallerySliderOverlay re-firing on new onChange ref
      }
    }
    setCurrentIndex(index);
  };

  return (
    <OpenXModal
      id={modalId}
      brand={brand}
      isMobile={isMobile}
      isOpen={isOpen === true}
      onClose={() => onDismiss()}
      fullScreen={true}
      className={`hotel-gallery-modal${view === 'photo' ? ' is-photo' : ''}`}
      aria-label={t('vacation.select.evo_gallery.photo_gallery') || 'Photo gallery'}
      disableBrowserHistoryNavigation={!onReopen}
      onCloseByBrowserHistory={onDismiss}
      onOpenByBrowserHistory={onReopen}
      bodyPadding={0}
      bodyPaddingS={0}
      bodyPaddingXS={0}
    >
      <Header $photo={view === 'photo'}>
        {view === 'photo' ? (
          // Fullscreen lightbox: single close (X) top-right, no title (matches Figma 979-41643)
          <CloseButton
            type="button"
            onClick={() => setView('grid')}
            aria-label={t('vacation.select.evo_gallery.back_to_gallery') || 'Back to gallery'}
          >
            <CloseIcon />
          </CloseButton>
        ) : (
          <CloseButton
            type="button"
            onClick={onDismiss}
            aria-label={t('vacation.select.evo_gallery.close_gallery') || 'Close gallery'}
          >
            <CloseIcon />
          </CloseButton>
        )}

        {view !== 'photo' && (
          <Text variant="Title4" forceTag="div">
            {t('vacation.select.evo_gallery.photos_title', { count: String(images.length) }) ||
              `Photos (${images.length})`}
          </Text>
        )}
      </Header>

      <Body>
        {view === 'grid' ? (
          <LeftArea ref={leftAreaRef} tabIndex={-1}>
            <GridView images={images} name={name} onPhotoClick={openPhoto} />
          </LeftArea>
        ) : (
          // Rendered outside the scrollable grid container (and flagged
          // data-swipe-dismiss-ignore) so the carousel's horizontal swipe isn't
          // intercepted by the grid's vertical scroll or the modal swipe-to-dismiss.
          <PhotoView
            images={images}
            name={name}
            currentIndex={currentIndex}
            sliderImageIndex={isMobile ? photoStartIndex : currentIndex}
            isMobile={isMobile}
            onSlideChange={onSlideChange}
            onNavigate={(delta) => {
              const next = (currentIndex + delta + images.length) % images.length;
              isInitializingRef.current = true;
              targetIndexRef.current = next;
              setCurrentIndex(next);
            }}
          />
        )}
      </Body>
    </OpenXModal>
  );
}

// #region sub-components

function GridView({
  images,
  name,
  onPhotoClick,
}: {
  images: string[];
  name?: string;
  onPhotoClick: (i: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <PhotoGrid>
      {images.map((src, i) => {
        const sources = getGridImageSources({
          url: src,
          responsiveOptions: [
            {
              media: customCss.breakpoints.lg,
              imageWidth: 476,
              imageHeight: 357,
              bookingResolution: 'max1280x900',
            },
            {
              media: customCss.breakpoints.sm,
              imageWidth: 340,
              imageHeight: 255,
              bookingResolution: 'max1024x768',
            },
            {
              media: customCss.breakpoints.xs,
              imageWidth: 230,
              imageHeight: 173,
              bookingResolution: 'max1024x768',
            },
          ],
        }).filter((source, idx, arr) => arr.findIndex((s) => s.srcSet === source.srcSet) === idx);

        // A button around the photo, not a focusable image, so it is announced as the control that
        // opens the fullscreen view (MPH-243).
        return (
          <PhotoButton key={src} type="button" onClick={() => onPhotoClick(i)}>
            <Picture
              url={src}
              sources={sources}
              alt={getPhotoAlt(t, name, i, images.length)}
              loading={i < 6 ? 'eager' : 'lazy'}
              pictureCss={css`
                display: block;
                width: 100%;
                height: 100%;
              `}
              css={css`
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 12px;
                background: ${({ theme }) => theme.kallisto.color.background.static.backgroundNeutralSubtle};
              `}
            />
          </PhotoButton>
        );
      })}
    </PhotoGrid>
  );
}

function PhotoView({
  images,
  name,
  currentIndex,
  sliderImageIndex,
  isMobile,
  onSlideChange,
  onNavigate,
}: {
  images: string[];
  name?: string;
  currentIndex: number;
  sliderImageIndex: number;
  isMobile: boolean;
  onSlideChange: (i: number) => void;
  onNavigate: (delta: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <PhotoViewWrap data-swipe-dismiss-ignore={true}>
      <SliderWrap>
        <ImageSliderFullScreen
          images={images}
          imageAltName={name}
          imageIndex={sliderImageIndex}
          transformations={{ url: getImageUrl, sources: getImageSources, sizes: getImagePictureSizes }}
          onSlideChange={onSlideChange}
          counterDisplay="none"
          // Mobile: swipeable scroll-snap carousel (no arrows). Desktop: static slide
          // navigated by the NavArrow buttons + keyboard.
          animation={isMobile ? 'scroll' : 'none'}
          css={css`
            height: 100%;

            & .image-slide {
              object-fit: contain;
              @media ${customCss.breakpoints.sm} {
                padding: 0 12%;
              }
            }

            & .slider-overlay-arrow-left,
            & .slider-overlay-arrow-right {
              display: none;
            }
          `}
        />

        {!isMobile && images.length > 1 && (
          <>
            <NavArrow
              $side="left"
              onClick={() => onNavigate(-1)}
              aria-label={t('vacation.select.evo_gallery.previous_photo') || 'Previous photo'}
            >
              <Icon category="Arrows" name="icon-arrow-left" size={20} color="iconPrimary" alt="" />
            </NavArrow>
            <NavArrow
              $side="right"
              onClick={() => onNavigate(1)}
              aria-label={t('vacation.select.evo_gallery.next_photo') || 'Next photo'}
            >
              <Icon category="Arrows" name="icon-arrow-left" size={20} color="iconPrimary" alt="" />
            </NavArrow>
          </>
        )}
      </SliderWrap>

      <Counter>
        {currentIndex + 1} / {images.length}
      </Counter>
    </PhotoViewWrap>
  );
}

// #endregion
